import { isMiniapp } from 'modules/miniapp/detect'

/**
 * Where a finished login lands — every scenario, without exception: the SMS
 * code, the 2FA password, the admin impersonate link, and the messenger
 * mini-app auto-login (signed contact, bot webhook, or a returning launch).
 *
 * Three of those used to land on /funds, which was the app's front door back
 * when home was a marketing page. Home now carries what you owe, so arriving
 * anywhere else means the first thing a member sees is a list instead of an
 * answer.
 *
 * Deliberately the ROOT path rather than `/home`. Some of these paths finish
 * with a document-level navigation, and Arvan serves deep paths with a 404
 * status — a browser renders the body anyway, but a mini-app webview honours
 * the status and shows «بارگیری با خطا مواجه شد». `/` always answers 200, and
 * `routes.tsx` redirects it to `/home` client-side. Keep it that way.
 */
export const POST_LOGIN_PATH = '/'

/**
 * Navigate to an in-app path, reloading the document only when that is safe.
 *
 * On the open web this is a plain `location.href` assignment — the historical
 * behaviour, which deliberately reloads so every consumer of auth state
 * (axios interceptor, AuthProvider, PostHog identity) re-reads storage.
 *
 * Inside a messenger mini app it must NOT reload. Arvan serves deep paths with
 * a 404 status (the body is the full index.html, so a browser renders it
 * anyway) and a mini-app webview honours the status instead of the body — Bale
 * shows «بارگیری با خطا مواجه شد» and the session is dead. So there we swap the
 * URL in place and let React Router re-render the same document.
 *
 * Imports `isMiniapp` from the leaf `detect` module on purpose: `modules/miniapp`
 * pulls in `services/miniapp` -> `services/axios`, and this helper is used *by*
 * the axios interceptor. `detect` imports nothing, so there is no cycle.
 */
export const navigateTo = (path: string) => {
    if (isMiniapp()) {
        window.history.replaceState(null, '', path)
        // React Router's browser history listens on popstate; replaceState
        // alone is silent, so tell it the location moved.
        window.dispatchEvent(new PopStateEvent('popstate'))
        return
    }
    window.location.href = path
}
